import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import Drawer from '@/components/ui/drawer'
import OrdersNavTabs from '@/components/orders/OrdersNavTabs'
import { contactsApi, type Contact } from '@/services/contactsApi'
import { listChannelInstances } from '@/services/channelsApi'
import { getProducts, type Product } from '@/lib/api/products'
import { ordersApi, type OrderResponse, type OrderStatus } from '@/services/ordersApi'
import EditOrderWizardModal, { type ContactSummary as EditContactSummary, type EditOrderForm } from './EditOrderWizardModal'
import OrderDetailView from './OrderDetailView'
import OrderCreateWizard from './OrderCreateWizard'
import OrderWhatsAppShareModal from './OrderWhatsAppShareModal'
import OrdersFilters from './OrdersFilters'
import OrdersTable from './OrdersTable'
import OrdersKanbanBoard from './OrdersKanbanBoard'
import {
  buildRepeatOrderUrl,
  formatOrderMoney,
  getOrdersEmptyState,
  isCancellationNoteValid,
  isDeliveryLikeMethod,
  isPickupLikeMethod,
  ORDER_CANCELLATION_REASONS,
} from './ordersUx'
import {
  AlertCircle,
  FileSpreadsheet,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  ShoppingCart,
} from 'lucide-react'
import { exportOrdersToExcel } from './ordersExcelExport'
import {
  defaultOrdersViewMode,
  getOrderPrimaryAction,
  ordersViewStorageKey,
  readOrdersViewMode,
  resolveVerticalOrdersConfig,
  useVerticalUI,
  writeOrdersViewMode,
  type OrdersViewMode,
} from '@/lib/verticalUI'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'
import { useOrderDetail, useOrdersList, useOrdersSummary, useUpdateOrderStatus } from './useOrdersData'
import {
  applyOrderStatusTab,
  buildOrderFiltersFromSearchParams,
  countActiveOrderFilters,
  DEFAULT_ORDER_FILTERS,
  filterOrdersByFulfillment,
  getOrderTabCounts,
  resolveOrderStatusTab,
  type FulfillmentFilterId,
  type OrdersFilterState,
} from './orderFilters'
import { resolveWaitThresholds } from './useTicketTimer'
import { getOrderHeaderLayoutClass } from './orderClassHelpers'
import { botSettingsApi } from '@/services/botSettingsApi'



function createEditOrderForm(order: OrderResponse): EditOrderForm {
  const source = order.source === 'catalog' || order.source === 'checkout' ? order.source : 'manual'
  return {
    source,
    contactId: order.contactId ?? '',
    conversationId: order.conversationId ?? '',
    channelInstanceId: order.channelInstance?.id ?? '',
    customerName: order.customerName ?? '',
    customerPhone: order.customerPhone ?? '',
    customerEmail: order.customerEmail ?? '',
    fulfillmentMethod: order.fulfillmentMethod ?? '',
    fulfillmentAddress: order.fulfillmentAddress ?? '',
    fulfillmentReference: order.fulfillmentReference ?? '',
    notes: order.notes ?? '',
    items: order.items.length > 0
      ? order.items.map((item) => ({ productId: item.productId ?? '', quantity: item.quantity }))
      : [{ productId: '', quantity: 1 }],
  }
}

function initialViewMode(): OrdersViewMode {
  if (typeof window === 'undefined') return 'list'
  return defaultOrdersViewMode(window.innerWidth)
}

export default function OrdersPage() {
  const { profile } = useVerticalUI()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id?: string }>()
  const [searchParams] = useSearchParams()
  const tenantId = useAuthStore((state) => state.tenantId)
  const createMode = id === 'new'
  const ordersConfig = resolveVerticalOrdersConfig(profile)
  const isKanbanVertical = ordersConfig.layout === 'kanban'

  const [filters, setFilters] = useState<OrdersFilterState>(() => buildOrderFiltersFromSearchParams(searchParams))
  const [page, setPage] = useState(1)
  const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentFilterId>('all')
  const [viewMode, setViewMode] = useState<OrdersViewMode>(initialViewMode)
  const [notifyOrder, setNotifyOrder] = useState<OrderResponse | null>(null)
  const [nextPendingSuggest, setNextPendingSuggest] = useState<OrderResponse | null>(null)
  const [statusNoteTarget, setStatusNoteTarget] = useState<{ orderId: string; status: 'completed' | 'cancelled' } | null>(null)
  const [statusNote, setStatusNote] = useState('')
  const [editTarget, setEditTarget] = useState<OrderResponse | null>(null)
  const [editForm, setEditForm] = useState<EditOrderForm>(() => ({
    source: 'manual',
    contactId: '',
    conversationId: '',
    channelInstanceId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    fulfillmentMethod: '',
    fulfillmentAddress: '',
    fulfillmentReference: '',
    notes: '',
    items: [{ productId: '', quantity: 1 }],
  }))
  const [editSelectedContact, setEditSelectedContact] = useState<EditContactSummary | null>(null)
  const [editContactSearch, setEditContactSearch] = useState('')
  const [editWizardStep, setEditWizardStep] = useState<1 | 2 | 3>(1)
  const [productSearch, setProductSearch] = useState('')

  const listFilters = { ...filters, statuses: filters.statuses }
  const ordersQuery = useOrdersList(listFilters, page)
  const ordersSummaryQuery = useOrdersSummary()
  const selectedOrderQuery = useOrderDetail(id)

  const channelInstancesQuery = useQuery({
    queryKey: queryKeys.orderChannelInstances(tenantId),
    queryFn: () => listChannelInstances(),
    staleTime: 60_000,
  })
  const botSettingsQuery = useQuery({
    queryKey: queryKeys.orderBotSettings(tenantId),
    queryFn: () => botSettingsApi.getSettings(),
    staleTime: 60_000,
  })
  const productsQuery = useQuery({
    queryKey: queryKeys.orderProducts(tenantId),
    queryFn: () => getProducts(),
    enabled: Boolean(editTarget),
  })
  const editContactsQuery = useQuery({
    queryKey: queryKeys.orderContacts(tenantId, `edit-${editContactSearch}`),
    queryFn: () => contactsApi.getContacts({ search: editContactSearch, limit: 8, page: 1 }),
    enabled: Boolean(editTarget),
    staleTime: 15_000,
  })
  const editRecentOrdersQuery = useQuery({
    queryKey: ['orders', tenantId ?? 'no-tenant', 'edit-recent', editForm.contactId || editTarget?.contactId],
    queryFn: () => ordersApi.getOrders({ contactId: (editForm.contactId || editTarget?.contactId) as string, page: 1, limit: 5 }),
    enabled: Boolean(editTarget && (editForm.contactId || editTarget.contactId)),
    staleTime: 20_000,
  })

  const statusMutation = useUpdateOrderStatus(id)

  const editMutation = useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: Parameters<typeof ordersApi.updateOrder>[1] }) =>
      ordersApi.updateOrder(orderId, payload),
    onSuccess: async (order) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.order(tenantId, order.id) })
      setEditTarget(null)
      toast.success(`Orden ${order.orderNumber} actualizada`)
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || error?.message || 'No se pudo editar la orden'),
  })

  const viewStorageKey = ordersViewStorageKey(tenantId, profile.slug)

  useEffect(() => {
    if (!isKanbanVertical || typeof window === 'undefined') return
    const stored = readOrdersViewMode(viewStorageKey)
    if (stored) setViewMode(stored)
    else setViewMode(defaultOrdersViewMode(window.innerWidth))
  }, [isKanbanVertical, viewStorageKey])

  useEffect(() => {
    if (!editTarget) return
    setEditForm(createEditOrderForm(editTarget))
    setEditSelectedContact(
      editTarget.contact
        ? {
            id: editTarget.contact.id,
            name: editTarget.contact.name,
            phone: editTarget.contact.phone ?? null,
            waContact: editTarget.contact.waContact ?? null,
            email: editTarget.contact.email ?? null,
          }
        : null
    )
    setEditContactSearch(editTarget.contact?.name || editTarget.customerName || editTarget.customerPhone || '')
    setEditWizardStep(1)
  }, [editTarget])

  useEffect(() => {
    setPage(1)
  }, [filters])

  useEffect(() => {
    setNextPendingSuggest(null)
  }, [id])

  const rawOrders = useMemo(() => ordersQuery.data?.orders ?? [], [ordersQuery.data])
  const orders = useMemo(
    () => filterOrdersByFulfillment(rawOrders, fulfillmentFilter, Boolean(profile.operational?.deliveryFilterEnabled)),
    [rawOrders, fulfillmentFilter, profile.operational?.deliveryFilterEnabled]
  )

  const availableProducts = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase()
    const base = availableProducts.slice()
    if (!term) return base.slice(0, 12)
    return base
      .filter((product) => [product.name, product.sku, product.description].filter(Boolean).join(' ').toLowerCase().includes(term))
      .slice(0, 12)
  }, [availableProducts, productSearch])

  const editSelectedProducts = useMemo(
    () =>
      editForm.items
        .map((item) => {
          const product = availableProducts.find((entry) => entry.id === item.productId)
          if (!product) return null
          return { ...product, quantity: item.quantity, lineTotalCents: product.priceCents * Math.max(1, item.quantity) }
        })
        .filter(Boolean) as Array<Product & { quantity: number; lineTotalCents: number }>,
    [availableProducts, editForm.items]
  )
  const editTotals = useMemo(
    () => ({ subtotal: editSelectedProducts.reduce((sum, product) => sum + product.lineTotalCents, 0), total: editSelectedProducts.reduce((sum, product) => sum + product.lineTotalCents, 0) }),
    [editSelectedProducts]
  )

  const editFulfillmentMode = editForm.fulfillmentMethod || 'delivery'
  const editIsDeliveryMode = isDeliveryLikeMethod(editFulfillmentMode)
  const editIsPickupMode = isPickupLikeMethod(editFulfillmentMode)
  const editChannelInstance = channelInstancesQuery.data?.find((instance) => instance.id === editForm.channelInstanceId) ?? editTarget?.channelInstance ?? null
  const editRecentOrders = editRecentOrdersQuery.data?.orders ?? []
  const pickupOptions = useMemo(() => {
    const zones = botSettingsQuery.data?.checkoutConfig?.shippingZones ?? []
    return zones.length > 0 ? zones : ['Sucursal principal', 'Sucursal centro', 'Retiro en depósito']
  }, [botSettingsQuery.data?.checkoutConfig?.shippingZones])

  const activeTab = resolveOrderStatusTab(filters)
  const activeFilterCount = countActiveOrderFilters(filters)
  const totalOrders = ordersQuery.data?.pagination.total ?? 0
  const totalPages = ordersQuery.data?.pagination.totalPages ?? 1
  const emptyState = getOrdersEmptyState({ total: totalOrders, hasActiveFilters: activeFilterCount > 0 })
  const tabCounts = getOrderTabCounts(ordersSummaryQuery.data)
  const selectedOrder = selectedOrderQuery.data ?? null
  const selectedPrimaryAction = selectedOrder ? getOrderPrimaryAction(selectedOrder.status, ordersConfig) : null
  const thresholds = resolveWaitThresholds(
    botSettingsQuery.data?.checkoutConfig,
    { warnMinutes: ordersConfig.waitWarnMinutes, criticalMinutes: ordersConfig.waitCriticalMinutes }
  )

  const goToOrder = (order: OrderResponse) => navigate(`/app/orders/${order.id}`)
  const closeDrawer = () => navigate('/app/orders')

  const openEdit = (order: OrderResponse) => {
    setEditTarget(order)
    if (id !== order.id) navigate(`/app/orders/${order.id}`, { replace: true })
  }

  const closeEditOrder = () => {
    if (editMutation.isPending) return
    setEditTarget(null)
    setEditSelectedContact(null)
    setEditContactSearch('')
    setEditWizardStep(1)
  }

  const advanceOrder = (order: OrderResponse, next: OrderStatus) => {
    if (next === 'completed' || next === 'cancelled') {
      setStatusNoteTarget({ orderId: order.id, status: next })
      setStatusNote('')
      return
    }
    statusMutation.mutate(
      { orderId: order.id, status: next },
      {
        onSuccess: () => {
          if (next !== 'confirmed') return
          const candidate = orders.find(
            (entry) =>
              entry.id !== order.id &&
              (entry.status === 'pending_confirmation' || entry.status === 'draft')
          )
          setNextPendingSuggest(candidate ?? null)
        },
      }
    )
  }

  const openCancel = (order: OrderResponse) => {
    setStatusNoteTarget({ orderId: order.id, status: 'cancelled' })
    setStatusNote('')
  }

  const closeStatusNoteModal = () => {
    if (statusMutation.isPending) return
    setStatusNoteTarget(null)
    setStatusNote('')
  }

  const submitStatusWithNote = () => {
    if (!statusNoteTarget) return
    if (!isCancellationNoteValid(statusNoteTarget.status, statusNote)) {
      toast.error('Indica el motivo de la cancelación')
      return
    }
    statusMutation.mutate({
      orderId: statusNoteTarget.orderId,
      status: statusNoteTarget.status,
      note: statusNote.trim() || undefined,
    })
    setStatusNoteTarget(null)
    setStatusNote('')
  }

  const submitEdit = () => {
    if (!editTarget) return
    const items = editForm.items
      .map((item) => ({ productId: item.productId.trim(), quantity: Math.max(1, Number(item.quantity || 1)) }))
      .filter((item) => item.productId)
    if (items.length === 0) {
      toast.error('Selecciona al menos un producto')
      return
    }
    editMutation.mutate({
      orderId: editTarget.id,
      payload: {
        contactId: editForm.contactId || undefined,
        customerName: editForm.customerName || undefined,
        customerPhone: editForm.customerPhone || undefined,
        customerEmail: editForm.customerEmail || undefined,
        fulfillmentMethod: editForm.fulfillmentMethod || undefined,
        fulfillmentAddress: editForm.fulfillmentAddress || undefined,
        fulfillmentReference: editForm.fulfillmentReference || undefined,
        notes: editForm.notes || undefined,
        items,
      },
    })
  }

  const handleExportExcel = () => {
    if (orders.length === 0) {
      toast.error('No hay órdenes para exportar con los filtros seleccionados')
      return
    }
    try {
      exportOrdersToExcel([...orders])
      toast.success(`Exportadas ${orders.length} órdenes a Excel`)
    } catch (error: any) {
      toast.error(error?.message || 'Error al exportar a Excel')
    }
  }

  const handleTabChange = (tab: Parameters<typeof applyOrderStatusTab>[1]) => {
    setFilters((current) => applyOrderStatusTab(current, tab))
  }

  const changeViewMode = (mode: OrdersViewMode) => {
    setViewMode(mode)
    writeOrdersViewMode(viewStorageKey, mode)
  }

  const primaryActionFor = (status: OrderStatus) => getOrderPrimaryAction(status, ordersConfig)

  if (createMode) {
    return <OrderCreateWizard onClose={() => navigate('/app/orders', { replace: true })} />
  }

  const header = (
    <div className="shrink-0 space-y-3">
      <div className={getOrderHeaderLayoutClass()}>
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
            <ShoppingCart className="h-3.5 w-3.5" />
            Operaciones comerciales
          </div>
          <h1 data-tour="orders-main" className="mt-1.5 text-xl font-black text-slate-900 sm:text-2xl">
            {profile.terminology.primaryTransactionName}
          </h1>
          <p className="mt-0.5 text-sm text-slate-600">
            {ordersQuery.isLoading
              ? `Cargando ${profile.terminology.primaryTransactionPlural.toLowerCase()}...`
              : `${totalOrders} ${profile.terminology.primaryTransactionPlural.toLowerCase()} en total`}
          </p>
        </div>
        <div data-tour="orders-actions" className="flex flex-wrap items-center gap-2">
          {isKanbanVertical ? (
            <div role="group" aria-label="Vista de comandas" className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button type="button" aria-pressed={viewMode === 'board'} onClick={() => changeViewMode('board')} className={['inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-3 text-xs font-semibold', viewMode === 'board' ? 'bg-slate-900 text-white' : 'text-slate-600'].join(' ')}>
                <LayoutGrid className="h-4 w-4" /> Tablero
              </button>
              <button type="button" aria-pressed={viewMode === 'list'} onClick={() => changeViewMode('list')} className={['inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-3 text-xs font-semibold', viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600'].join(' ')}>
                <List className="h-4 w-4" /> Lista
              </button>
            </div>
          ) : null}
          <Button type="button" variant="outline" onClick={handleExportExcel} className="min-h-[44px]" title={`Exportar ${profile.terminology.primaryTransactionPlural.toLowerCase()} a Excel`}>
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Exportar
          </Button>
          <Button type="button" onClick={() => navigate('/app/orders/new')} className="min-h-[44px] bg-brand text-white hover:bg-brand-dark">
            <Plus className="mr-2 h-4 w-4" />
            {profile.terminology.newTransactionCta || 'Nueva orden'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total</p>
          <p className="mt-0.5 text-lg font-bold text-slate-900">{ordersSummaryQuery.data?.total ?? totalOrders}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Pendientes</p>
          <p className="mt-0.5 text-lg font-bold text-amber-800">{ordersSummaryQuery.data?.pending.count ?? '—'}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">En proceso</p>
          <p className="mt-0.5 text-lg font-bold text-blue-800">{ordersSummaryQuery.data?.active.count ?? '—'}</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Completadas hoy</p>
          <p className="mt-0.5 text-lg font-bold text-emerald-800">{ordersSummaryQuery.data?.completedToday.count ?? '—'}</p>
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      <OrdersNavTabs className="!mb-0" />
      {header}

      <div data-tour="orders-filters" className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <OrdersFilters
          filters={filters}
          onChange={setFilters}
          activeTab={activeTab}
          counts={tabCounts}
          onTabChange={handleTabChange}
          channelInstances={channelInstancesQuery.data ?? []}
          deliveryFilterEnabled={Boolean(profile.operational?.deliveryFilterEnabled)}
          fulfillmentFilter={fulfillmentFilter}
          onFulfillmentChange={setFulfillmentFilter}
          activeFilterCount={activeFilterCount}
          onClear={() => setFilters(DEFAULT_ORDER_FILTERS)}
          searchPlaceholder={`${profile.terminology.primaryTransactionSingular}, contacto, teléfono o correo`}
        />
      </div>

      <div data-tour="orders-list" className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {isKanbanVertical && viewMode === 'board' ? (
          <div className="p-3">
            <OrdersKanbanBoard
              orders={orders}
              columns={ordersConfig.columns}
              thresholds={thresholds}
              isLoading={ordersQuery.isLoading}
              isError={ordersQuery.isError}
              onRetry={() => ordersQuery.refetch()}
              primaryActionFor={primaryActionFor}
              updatingOrderId={statusMutation.isPending ? statusMutation.variables?.orderId ?? null : null}
              onOpen={goToOrder}
              onAdvance={advanceOrder}
              onEdit={openEdit}
              onNotify={setNotifyOrder}
              onCancel={openCancel}
              onCreate={() => navigate('/app/orders/new')}
              onViewHistory={() => {
                changeViewMode('list')
                handleTabChange('completed')
              }}
            />
          </div>
        ) : (
          <OrdersTable
            orders={orders}
            isLoading={ordersQuery.isLoading}
            isError={ordersQuery.isError}
            onRetry={() => ordersQuery.refetch()}
            emptyState={emptyState}
            hasActiveFilters={activeFilterCount > 0}
            primaryActionFor={primaryActionFor}
            onOpen={goToOrder}
            onEdit={openEdit}
            onNotify={setNotifyOrder}
            onCancel={openCancel}
            onAdvance={advanceOrder}
            updatingOrderId={statusMutation.isPending ? statusMutation.variables?.orderId ?? null : null}
            onClearFilters={() => setFilters(DEFAULT_ORDER_FILTERS)}
            onGoConversations={() => navigate('/app/conversations')}
            onGoProducts={() => navigate('/app/products')}
            onCreate={() => navigate('/app/orders/new')}
            page={page}
            totalPages={totalPages}
            total={totalOrders}
            onPageChange={setPage}
          />
        )}
      </div>

      <Drawer
        isOpen={Boolean(id && id !== 'new')}
        onClose={closeDrawer}
        title={selectedOrder ? `Orden ${selectedOrder.orderNumber}` : 'Detalle de la orden'}
        description={selectedOrder?.customerName || selectedOrder?.customerPhone || undefined}
        widthClassName="sm:max-w-3xl"
        actions={
          selectedOrder && selectedPrimaryAction ? (
            <Button
              type="button"
              size="sm"
              disabled={statusMutation.isPending}
              onClick={() => advanceOrder(selectedOrder, selectedPrimaryAction.next)}
              className="whitespace-nowrap bg-brand text-white hover:bg-brand-dark"
            >
              {statusMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {selectedPrimaryAction.label}
            </Button>
          ) : undefined
        }
      >
        {selectedOrderQuery.isError ? (
          <div className="flex h-full items-center justify-center p-8 text-center">
            <div className="max-w-sm">
              <AlertCircle className="mx-auto h-12 w-12 text-rose-300" />
              <p className="mt-3 text-lg font-semibold text-slate-900">No pudimos cargar esta orden</p>
              <p className="mt-1 text-sm text-slate-500">Puede que haya sido eliminada o que no tengas acceso.</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button type="button" variant="outline" onClick={closeDrawer}>Volver al listado</Button>
                <Button type="button" onClick={() => selectedOrderQuery.refetch()}>Reintentar</Button>
              </div>
            </div>
          </div>
        ) : selectedOrderQuery.isLoading ? (
          <div className="flex h-64 items-center justify-center text-slate-500" role="status">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando detalle...
          </div>
        ) : selectedOrder ? (
          <>
            <OrderDetailView
              order={selectedOrder}
              onEdit={() => openEdit(selectedOrder)}
              onRepeat={() => navigate(buildRepeatOrderUrl(selectedOrder))}
              onUpdateStatus={(newStatus) => advanceOrder(selectedOrder, newStatus)}
              onOpenStatusModal={(targetStatus) => {
                setStatusNoteTarget({ orderId: selectedOrder.id, status: targetStatus })
                setStatusNote('')
              }}
              isUpdatingStatus={statusMutation.isPending}
              onBack={closeDrawer}
              showNextAction={false}
            />
            {nextPendingSuggest ? (
              <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold text-slate-700">Siguiente pendiente</p>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate text-sm text-slate-800">
                    #{nextPendingSuggest.orderNumber} · {nextPendingSuggest.customerName || nextPendingSuggest.customerPhone || 'Sin cliente'}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    className="min-h-[40px] shrink-0 bg-brand text-white hover:bg-brand-dark"
                    onClick={() => {
                      setNextPendingSuggest(null)
                      goToOrder(nextPendingSuggest)
                    }}
                  >
                    Abrir
                  </Button>
                </div>
              </div>
            ) : null}
            {id && id !== 'new' && selectedOrder.status !== 'completed' && selectedOrder.status !== 'cancelled' ? (
              <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
                <Button type="button" variant="outline" className="w-full border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => openCancel(selectedOrder)}>
                  Cancelar orden
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <EditOrderWizardModal
        isOpen={Boolean(editTarget)}
        onClose={closeEditOrder}
        order={editTarget}
        wizardStep={editWizardStep}
        setWizardStep={setEditWizardStep}
        goToNextStep={() => setEditWizardStep((current) => (current < 3 ? ((current + 1) as 1 | 2 | 3) : current))}
        goToPreviousStep={() => setEditWizardStep((current) => (current > 1 ? ((current - 1) as 1 | 2 | 3) : current))}
        form={editForm}
        setForm={setEditForm}
        selectedContact={editSelectedContact}
        onSelectContact={(contact: Contact) => {
          setEditSelectedContact({
            id: contact.id,
            name: contact.name,
            phone: contact.phone ?? null,
            waContact: contact.waContact ?? null,
            email: contact.email ?? null,
          })
          setEditForm((current) => ({
            ...current,
            contactId: contact.id,
            customerName: contact.name || current.customerName,
            customerPhone: contact.phone || contact.waContact || current.customerPhone,
            customerEmail: contact.email || current.customerEmail,
          }))
        }}
        contactSearch={editContactSearch}
        setContactSearch={setEditContactSearch}
        contacts={editContactsQuery.data?.data ?? []}
        productSearch={productSearch}
        setProductSearch={setProductSearch}
        filteredProducts={filteredProducts}
        availableProducts={availableProducts}
        addProductToOrder={(product) =>
          setEditForm((current) => {
            const existing = current.items.findIndex((item) => item.productId === product.id)
            if (existing >= 0) {
              return { ...current, items: current.items.map((item, index) => (index === existing ? { ...item, quantity: Math.max(1, item.quantity + 1) } : item)) }
            }
            return { ...current, items: [...current.items.filter((item) => item.productId), { productId: product.id, quantity: 1 }] }
          })
        }
        addEmptyItem={() => setEditForm((current) => ({ ...current, items: [...current.items, { productId: '', quantity: 1 }] }))}
        updateItem={(index, patch) => setEditForm((current) => ({ ...current, items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }))}
        removeItem={(index) => setEditForm((current) => {
          const nextItems = current.items.filter((_, itemIndex) => itemIndex !== index)
          return { ...current, items: nextItems.length > 0 ? nextItems : [{ productId: '', quantity: 1 }] }
        })}
        appendNoteTemplate={(template) => setEditForm((current) => ({ ...current, notes: current.notes ? `${current.notes}\n${template}` : template }))}
        selectedProducts={editSelectedProducts}
        totals={editTotals}
        pickupOptions={pickupOptions}
        editChannelInstance={editChannelInstance}
        recentOrders={editRecentOrders}
        isDeliveryMode={editIsDeliveryMode}
        isPickupMode={editIsPickupMode}
        fulfillmentMode={editFulfillmentMode}
        stepOneReady={Boolean(editForm.contactId || editForm.customerName.trim() || editForm.customerPhone.trim())}
        stepTwoReady={editForm.items.length > 0 && editForm.items.every((item) => item.productId.trim())}
        stepThreeReady={(!editIsDeliveryMode || Boolean(editForm.fulfillmentAddress.trim())) && (!editIsPickupMode || Boolean(editForm.fulfillmentReference.trim()))}
        isSaving={editMutation.isPending}
        onSubmit={submitEdit}
        money={formatOrderMoney}
        when={(value) => (value ? new Date(value).toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Sin fecha')}
      />

      <Modal
        isOpen={Boolean(statusNoteTarget)}
        onClose={closeStatusNoteModal}
        title={statusNoteTarget?.status === 'cancelled' ? 'Cancelar orden' : 'Completar orden'}
        size="md"
      >
        {statusNoteTarget ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {statusNoteTarget.status === 'cancelled'
                ? 'El motivo es obligatorio para dejar trazabilidad al cliente.'
                : 'Si quieres, deja una observación breve antes de cerrar la orden.'}
            </p>
            {statusNoteTarget.status === 'cancelled' ? (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Motivos frecuentes</p>
                <div className="flex flex-wrap gap-1.5">
                  {ORDER_CANCELLATION_REASONS.map((reason) => (
                    <button key={reason} type="button" onClick={() => setStatusNote(reason)} className={['rounded-full border px-2.5 py-1 text-xs transition', statusNote === reason ? 'border-rose-400 bg-rose-50 font-semibold text-rose-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'].join(' ')}>
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <textarea
              value={statusNote}
              onChange={(event) => setStatusNote(event.target.value)}
              rows={4}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder={statusNoteTarget.status === 'cancelled' ? 'Motivo de la cancelación' : 'Observación opcional'}
              aria-label={statusNoteTarget.status === 'cancelled' ? 'Motivo de la cancelación' : 'Observación de la orden'}
            />
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <Button type="button" variant="outline" onClick={closeStatusNoteModal} disabled={statusMutation.isPending}>Cancelar</Button>
              <Button
                type="button"
                className={statusNoteTarget.status === 'cancelled' ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-brand text-white hover:bg-brand-dark'}
                onClick={submitStatusWithNote}
                disabled={statusMutation.isPending || !isCancellationNoteValid(statusNoteTarget.status, statusNote)}
              >
                {statusMutation.isPending ? 'Guardando...' : statusNoteTarget.status === 'cancelled' ? 'Cancelar orden' : 'Completar'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {notifyOrder ? (
        <OrderWhatsAppShareModal isOpen onClose={() => setNotifyOrder(null)} order={notifyOrder} />
      ) : null}
    </div>
  )
}
